import * as React from "react";
import {
  Tabs as AriaTabs,
  TabList as AriaTabList,
  Tab as AriaTab,
  TabPanel as AriaTabPanel,
  type TabsProps as AriaTabsProps,
  type TabListProps as AriaTabListProps,
  type TabProps as AriaTabProps,
  type TabPanelProps as AriaTabPanelProps,
} from "react-aria-components";

import { cn } from "./utils";

export interface TabsProps extends Omit<AriaTabsProps, "className"> {
  className?: string;
  /** Radix-compat alias for defaultSelectedKey */
  defaultValue?: string;
  /** Radix-compat alias for selectedKey */
  value?: string;
  /** Radix-compat alias for onSelectionChange */
  onValueChange?: (value: string) => void;
}

function Tabs({ className, defaultValue, value, onValueChange, ...props }: TabsProps) {
  return (
    <AriaTabs
      data-slot="tabs"
      className={cn("flex flex-col gap-2", className)}
      defaultSelectedKey={defaultValue ?? props.defaultSelectedKey}
      selectedKey={value ?? props.selectedKey}
      onSelectionChange={onValueChange
        ? (key) => onValueChange(String(key))
        : props.onSelectionChange}
      {...props}
    />
  );
}

export interface TabsListProps extends Omit<AriaTabListProps<object>, "className"> {
  className?: string;
}

function TabsList({ className, ...props }: TabsListProps) {
  return (
    <AriaTabList
      data-slot="tabs-list"
      className={cn(
        "bg-muted text-muted-foreground inline-flex h-9 w-fit items-center justify-center rounded-xl p-[3px] flex",
        className,
      )}
      {...props}
    />
  );
}

export interface TabsTriggerProps extends Omit<AriaTabProps, "className" | "id"> {
  className?: string;
  /** Radix-compat alias for id */
  value?: string;
  id?: AriaTabProps["id"];
}

function TabsTrigger({ className, value, ...props }: TabsTriggerProps) {
  return (
    <AriaTab
      data-slot="tabs-trigger"
      id={value ?? props.id}
      className={cn(
        "selected:bg-card dark:selected:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring dark:selected:border-input dark:selected:bg-input/30 text-foreground dark:text-muted-foreground inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-xl border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

export interface TabsContentProps extends Omit<AriaTabPanelProps, "className" | "id"> {
  className?: string;
  /** Radix-compat alias for id */
  value?: string;
  id?: AriaTabPanelProps["id"];
}

function TabsContent({ className, value, ...props }: TabsContentProps) {
  return (
    <AriaTabPanel
      data-slot="tabs-content"
      id={value ?? props.id}
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
